import { BotConversation, BotResponse, ConversationMessage, ConversationType, InputInfo, isAssistantConversation, LucaEngListModel, LucaFileModel, ThreadInterface, ToolInputEnum, ToolStep, ToolStepInfo, ToolTypeEnum } from 'countable@model';
import { SearchThreadInterface } from '../model/luka/search-thread.interface';

export class LukaInputUtil {

  static setArgumentsBasedOnPrompt(eng: LucaEngListModel, toolStep: ToolStep = null, inputInfo: InputInfo = null,
    hasFilesUploaded: boolean = false): Record<string, any> {
    const inputArguments: Record<string, any> = {
      integration_id: eng?.integrationId ?? 0,
      activeStep: toolStep,
      active_step: toolStep,
      client_name: eng?.clientName,
      engagement_name: eng?.engName,
      engagement_type_id: eng?.engTypeId,
      year_end_date: eng?.engYearEnd,
      client_firm_id: eng?.clientId,
      selected_tb_account_ids: inputInfo?.selected_tb_account_ids,
      startDate: inputInfo?.startDate,
      endDate: inputInfo?.endDate,
    };

    switch (toolStep) {
      case 'variance_analysis_input':
        inputArguments.change_percent_input = inputInfo?.changePercent;
        inputArguments.change_dollar_input = inputInfo?.changeValue;
        break;
      case 'loan_amortization_input':
        inputArguments.s3_file_details = inputInfo?.fileInfo?.map(f => f?.filePath);
        break;
      case 'bank_recon_selection':
        inputArguments.has_plaid = inputInfo?.br_inputs?.hasPlaid;
        inputArguments.tbAccountId = inputInfo?.br_inputs?.tbAccountId;
        inputArguments.plaidAccountId = inputInfo?.br_inputs?.plaidAccountId;
        break;
      case 'loan_amortization_inputs':
        inputArguments.hasMissingInputs = true;
        inputArguments.active_step = ToolStep.LOAN_AMORTIZATION;
        inputArguments.loanInfo = {
          loanStartDate: inputInfo?.loan_inputs?.loanStartDate,
          frequency: inputInfo?.loan_inputs?.frequency,
          loanTenure: inputInfo?.loan_inputs?.loanTenure,
          principalAmount: inputInfo?.loan_inputs?.principalAmount,
          interestRate: inputInfo?.loan_inputs?.interestRate
        };
        break;
      case 'investment_schedule_input':
        inputArguments.year_end = eng?.engYearEnd;
        inputArguments.confirmation = inputInfo?.confirmation;
        break;
      case 'investment_schedule_file_analysis':
        inputArguments.year_end = eng?.engYearEnd;
        inputArguments.confirmation = inputInfo?.confirmation;
        break;
      case 'hst_analysis_input':
        inputArguments.year_end = eng?.engYearEnd;
        inputArguments.confirmation = inputInfo?.confirmation;
        break;
      case 'capital_asset_input':
        inputArguments.input_data = inputInfo?.cass_inputs?.accounts;
        inputArguments.active_step = 'capital_asset_schedule';
        break;
      case 'bank_statements_to_tb_input':
        inputArguments.year = eng?.engYearEnd;
        break;
      case 'payroll_input_request':
        inputArguments.year_end = eng?.engYearEnd;
        inputArguments.confirmation = inputInfo?.confirmation;
        break;
      case 'data_extraction':
      case 'rent_input_request':
        inputArguments.selected_tb_account_ids = inputInfo?.selected_tb_account_ids;
        break;
      case 'rent_process_routing':
        inputArguments.rentInfo = inputInfo?.rentInfo;
        inputArguments.options = inputInfo?.options;
        inputArguments.scenario = inputInfo?.scenario;
        break;
      case 'process_user_message':
        inputArguments.payrollInfo = inputInfo?.payrollInfo;
        inputArguments.selected_tb_account_ids = inputInfo?.selected_tb_account_ids;
        inputArguments.hasToolStepFiles = hasFilesUploaded;
        break;
      case 'request_selection':
        inputArguments.selected_tb_account_ids = inputInfo?.selected_tb_account_ids;
        inputArguments.hasToolStepFiles = hasFilesUploaded;
        break;
      case 'final_tb_output_table':
        inputArguments.transactions_data = inputInfo?.transactional_table;
        break;
      case 'covenant_input_information':
        inputArguments.active_step = 'process_user_message';
        break;
      case 'fetch_and_validate_tb_interest':
        inputArguments.active_step = 'ocr_and_file_validation';
        break;
      case 'proceed_interest_reasonability':
        inputArguments.agreement_info = inputInfo?.agreement_info;
        break;

    }

    return inputArguments;
  }

  static setErrorResponse(error: string) {
    return {
      botResponseMarkdown: error,
      botResponseType: "MARKDOWN",
      messageType: ConversationType.SYSTEM
    } as BotConversation
  }

  static setMessageType(response: BotResponse) {
    if(response?.inputType === ToolInputEnum.GENERAL_LEDGER) {
      return ConversationType.GENERAL_LEDGER;
    }
    if ((response?.hasInputs && response?.activeTool === ToolTypeEnum.PA && response?.activeStep === ToolStep.PAYROLL_INFO) ||
      (response?.hasInputs && response?.activeTool === ToolTypeEnum.RR && response?.activeStep === ToolStep.RENT_INFO) ||
      (response?.hasInputs && response?.activeTool === ToolTypeEnum.CT && response?.activeStep === ToolStep.COVENANT_INFO)) {
      return ConversationType.SYSTEM;
    }
    if(response?.inputType === ToolInputEnum.WORKFLOW && response?.activeTool === ToolTypeEnum.RR && response?.activeStep === ToolStep.RENT_INPUT) {
      return ConversationType.WORKFLOW
    }
    if((response?.inputType === ToolInputEnum.SELECTION_AND_FILE) ||
      (response?.hasInputs && response?.activeTool === ToolTypeEnum.PA &&
        (response?.activeStep === ToolStep.PAYROLL_INFO || response?.activeStep === ToolStep.PAYROLL_ANALYSIS))) {
      return ConversationType.WORKFLOW;
    }
    return (response?.hasInputs && (
      ((response?.inputType === ToolInputEnum.WORKFLOW) || (response?.inputType === ToolInputEnum.FILE) || (response?.inputType === ToolInputEnum.SELECTION)
        || (response?.activeStep === ToolStep.CAA_SCHEDULE) ||
        (response?.hasInputs) || response?.inputType === ToolInputEnum.FILES)
    )) ? ConversationType.WORKFLOW : ConversationType.SYSTEM;
  }

  static sortThreads(list: ThreadInterface[]) {
    if (!list || !Array.isArray(list)) {
      return [];
    }
    const threads = list?.filter(t => !!t?.chat_session_id);
    if (!threads || !Array.isArray(threads)) {
      return [];
    }
    const pinnedThreads = threads
      .filter(thread => thread.isPinned)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const unpinnedThreads = threads
      .filter(thread => !thread.isPinned)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return [...pinnedThreads, ...unpinnedThreads];
  }

  static isFirefox(): boolean {
    const userAgent = navigator?.userAgent?.toLowerCase();
    return userAgent?.includes('firefox');
  }

  static setToolStepInfo(toolStep: ToolStep = null, input: InputInfo = null): ToolStepInfo {
    if (!toolStep || !input) {
      return null;
    }
    const promptInputSteps = [ToolStep.VARIANCE_ANALYSIS, ToolStep.LOAN_AMOR_INPUTS, ToolStep.LOAN_AMORTIZATION,
    ToolStep.INVESTMENT_SCHEDULE, ToolStep.IS_ANALYSIS, ToolStep.CAAS_INPUT, ToolStep.BANK_STATEMENTS, ToolStep.BANK_RECON,
    ToolStep.RENT_INPUT, ToolStep.RENT_SELECTION, ToolStep.DATA_EXTRACTION, ToolStep.PAYROLL_ANALYSIS, ToolStep.USER_SELECTION];
    const promptUpdateSteps = [ToolStep.VARIANCE_ANALYSIS, ToolStep.LOAN_AMOR_INPUTS, ToolStep.LOAN_AMORTIZATION, ToolStep.INVESTMENT_SCHEDULE,
    ToolStep.IS_ANALYSIS, ToolStep.CAAS_INPUT, ToolStep.BANK_STATEMENTS, ToolStep.BANK_RECON,ToolStep.RENT_INPUT, ToolStep.RENT_SELECTION,
    ToolStep.DATA_EXTRACTION, ToolStep.PAYROLL_ANALYSIS, ToolStep.USER_SELECTION];
    return {
      hasPromptInput: promptInputSteps?.includes(toolStep),
      updateInformation: promptUpdateSteps?.includes(toolStep),
      inputInfo: { ...input }
    };
  }

  static mapEngAndClientName(threads: SearchThreadInterface[], engagementList: LucaEngListModel[]): SearchThreadInterface[] {
    if (!threads || !engagementList) return [];
    return threads?.map(thread => {
      const eng = engagementList.find(e => e.engId === thread?.engagementId);
      return {
        ...thread,
        engName: eng?.engName || '',
        clientName: eng?.clientName || '',
        engYear: eng?.yearEnd
      } as SearchThreadInterface
    });
  }

  static searchThreadsByName(threads: any[], searchTerm: string): any[] {
    if (!searchTerm?.trim()) return threads;
    const lowerTerm = searchTerm.toLowerCase();
    return threads.filter(t => t.threadName?.toLowerCase().includes(lowerTerm));
  }

  static mapThreadsByEngName(threads: ThreadInterface[], engagements: LucaEngListModel[]) {
    if (!threads || !engagements) return [];
    return threads?.map(thread => {
      const eng = engagements?.find(e => e.engId === thread.engagementId);
      return {
        ...thread,
        engName: eng?.engName || '',
        clientName: eng?.clientName || '',
        threadInfo: eng?.engName ? `${thread?.threadName} \n ${eng?.engName} \n ${thread?.updatedDate}` : `${thread?.threadName} \n ${thread?.updatedDate}`
      } as ThreadInterface
    });
  }

  static mapFilesToRequest(input: InputInfo,files: LucaFileModel[]) {
    const fileInput = input?.fileInfo?.map(f => {
      return { path: f?.filePath, id: f?.fileId, name: f?.fileName, user_expectation: 'interaction' };
    }) ?? [];
    const fileAttached = files?.map(file => ({
      path: file.filePath,
      user_expectation: 'interaction',
      id: file.fileId,
      name: file?.fileName
    })) ?? [];

    return [...fileInput, ...fileAttached];
  }

  static provideInitials(name: string = '') {
    if (!name) {
      return '';
    }
    const words = name.trim().split(/\s+/);
    let initials = '';
    for (const word of words) {
      if (word.length > 0) {
        initials += word[0].toUpperCase();
      }
      if (initials.length === 2) {
        break;
      }
    }
    return initials;
  }

  static getLastActiveStep(messages: ConversationMessage[]) {
    const last = messages[messages.length - 1];
    if(isAssistantConversation(last)) {
      return last?.activeStep;
    }
    return null
  }

}
